import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  LayoutDashboard, Users, Award, DollarSign, Calendar, FileText, LogOut, 
  Globe, CreditCard, MessageSquare, BarChart3, Crown, TrendingUp, Camera, 
  Package, ChevronDown, ChevronRight, Settings, Sparkles, CalendarCheck, 
  Menu, X, Brain, Building2, UsersRound, Stethoscope, UserCog, 
  Gift, ClipboardCheck, Lock, Zap, ImageIcon, Shield, Share2
} from 'lucide-react';
import NotificationsDropdown from '../../NotificationsDropdown';
import { usePermissions } from '@/context/PermissionContext';
import { playSubscriptionSound } from '@/utils/notifications';
import { useTheme } from '@/context/ThemeContext';
import ThemeToggle from '@/components/ThemeToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map routes to required feature keys from subscription tier
const ROUTE_TO_FEATURE = {
  '/admin': null, // Always available
  '/admin/analytics': 'basic_reporting',
  '/admin/reports': 'advanced_analytics',
  '/admin/ai-analytics': 'ai_analytics_dashboard',
  '/admin/members': 'basic_membership_management',
  '/admin/appointments': 'appointment_scheduling',
  '/admin/consultations': 'consultations',
  '/admin/messages': 'secure_messaging',
  '/admin/plans': 'basic_membership_management',
  '/admin/templates': 'membership_templates',
  '/admin/packages': 'treatment_packages',
  '/admin/fees': 'service_fees',
  '/admin/payments': 'payment_processing',
  '/admin/services': 'basic_membership_management',
  '/admin/providers': 'basic_membership_management',
  '/admin/benefits': 'basic_membership_management',
  '/admin/fulfillment': 'basic_membership_management',
  '/admin/before-after': 'before_after_gallery',
  '/admin/wordpress': 'wordpress_plugin',
  '/admin/settings': null, // Always available
  '/admin/branding': 'custom_branding',
  '/admin/team': 'team_management',
  '/admin/subscription': null, // Always available
  '/admin/stripe-connect': 'stripe_connect',
  '/admin/payment-gateways': 'payment_processing',
  '/admin/audit': 'audit_logs',
  '/admin/roles': 'role_based_access'
};

// Fallback tier levels for features not in the features object
const FEATURE_TIERS = {
  '/admin': 1,
  '/admin/analytics': 1,
  '/admin/reports': 2,
  '/admin/ai-analytics': 3,
  '/admin/members': 1,
  '/admin/appointments': 1,
  '/admin/consultations': 2,
  '/admin/messages': 2,
  '/admin/plans': 1,
  '/admin/templates': 2,
  '/admin/packages': 2,
  '/admin/fees': 1,
  '/admin/payments': 1,
  '/admin/services': 1,
  '/admin/providers': 1,
  '/admin/benefits': 2,
  '/admin/fulfillment': 2,
  '/admin/before-after': 2,
  '/admin/wordpress': 3,
  '/admin/settings': 1,
  '/admin/branding': 2,
  '/admin/team': 1,
  '/admin/subscription': 1,
  '/admin/stripe-connect': 2,
  '/admin/payment-gateways': 1,
  '/admin/audit': 3
};

const TIER_LEVELS = {
  'starter': 1,
  'basic': 1,
  'free': 1,
  'trial': 1,
  'professional': 2,
  'pro': 2,
  'growth': 3,
  'enterprise': 3,
  'premium': 3
};

const TIER_COLORS = {
  'starter': 'bg-gray-100 text-gray-700 border-gray-300',
  'basic': 'bg-gray-100 text-gray-700 border-gray-300',
  'free': 'bg-gray-100 text-gray-700 border-gray-300',
  'trial': 'bg-gray-100 text-gray-700 border-gray-300',
  'professional': 'bg-blue-100 text-blue-700 border-blue-300',
  'pro': 'bg-blue-100 text-blue-700 border-blue-300',
  'growth': 'bg-purple-100 text-purple-700 border-purple-300',
  'enterprise': 'bg-purple-100 text-purple-700 border-purple-300',
  'premium': 'bg-purple-100 text-purple-700 border-purple-300'
};

const AdminSidebar = ({ user, onLogout }) => {
  const location = useLocation();
  const { canView, isAdmin, loading: permissionsLoading } = usePermissions();
  const { mode } = useTheme();
  const [expandedGroups, setExpandedGroups] = useState(['main']);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [clinicData, setClinicData] = useState(null);
  const [clinicTier, setClinicTier] = useState('starter');
  const [clinicTierName, setClinicTierName] = useState('Starter'); // Display name for the badge
  const [tierLevel, setTierLevel] = useState(1);
  const [tierFeatures, setTierFeatures] = useState({});
  const [tierBadgeAnimating, setTierBadgeAnimating] = useState(false);
  const previousTierRef = useRef(null);
  const [brandingColors, setBrandingColors] = useState({
    primary: '#14b8a6',
    secondary: '#0d9488'
  });
  
  const isDark = mode === 'dark';

  const fetchClinicData = async (showNotification = false, retryCount = 0) => {
    try {
      const token = localStorage.getItem('token');
      
      // If no token and we haven't retried yet, wait a bit and retry
      // This handles race conditions after login
      if (!token) {
        if (retryCount < 3) {
          console.log('No token found, retrying in 500ms...');
          setTimeout(() => fetchClinicData(showNotification, retryCount + 1), 500);
          return;
        }
        console.error('No token available after retries');
        return;
      }
      
      // Fetch clinic info and branding
      const [clinicRes, brandingRes] = await Promise.all([
        axios.get(`${API_URL}/api/clinic/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch((err) => {
          console.error('Failed to fetch clinic profile:', err?.response?.status);
          return null;
        }),
        axios.get(`${API_URL}/api/clinic/branding`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch((err) => {
          console.error('Failed to fetch clinic branding:', err?.response?.status);
          return null;
        })
      ]);
      
      if (clinicRes?.data) {
        setClinicData(clinicRes.data);
      } else if (retryCount < 2) {
        // Retry once if clinic data is null
        console.log('Clinic data null, retrying...');
        setTimeout(() => fetchClinicData(showNotification, retryCount + 1), 1000);
        return;
      }
      
      if (brandingRes?.data) {
        setBrandingColors({
          primary: brandingRes.data.primary_color || '#14b8a6',
          secondary: brandingRes.data.secondary_color || '#0d9488'
        });
      }

      // Fetch subscription info
      const subRes = await axios.get(`${API_URL}/api/clinic-subscription/current`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => null);
      
      if (subRes?.data) {
        const subscription = subRes.data?.subscription;
        const tier = subRes.data?.tier;
        const tierSlug = tier?.slug || subscription?.tier_slug || subscription?.plan_name || 'starter';
        const newTier = tierSlug.toLowerCase();
        const newTierName = tier?.name || tierSlug;
        
        // Check if tier changed and show notification/animation
        if (showNotification && previousTierRef.current && previousTierRef.current !== newTier) {
          const isUpgrade = (TIER_LEVELS[newTier] || 1) > (TIER_LEVELS[previousTierRef.current] || 1);
          
          // Trigger animation
          setTierBadgeAnimating(true);
          setTimeout(() => setTierBadgeAnimating(false), 2000);
          
          // Play sound effect (respects user's notification settings)
          playSubscriptionSound(isUpgrade);
          
          // Show toast notification
          if (isUpgrade) {
            toast.success(`Upgraded to ${newTierName}!`, {
              description: 'Your new features are now active.',
              icon: <Zap className="h-4 w-4 text-yellow-500" />
            });
          } else {
            toast.info(`Switched to ${newTierName}`, {
              description: 'Your plan has been updated.',
              icon: <Crown className="h-4 w-4 text-blue-500" />
            });
          }
        }
        
        previousTierRef.current = newTier;
        setClinicTier(newTier);
        setClinicTierName(newTierName); // Set the display name
        setTierLevel(TIER_LEVELS[newTier] || 1);
        
        // Store the actual features from the tier
        const features = tier?.features || subscription?.features || {};
        setTierFeatures(features);
      }
    } catch (error) {
      console.error('Error fetching clinic data:', error);
    }
  };

  useEffect(() => {
    // Only fetch data if user is logged in (has a token)
    const token = localStorage.getItem('token');
    if (token && user) {
      fetchClinicData(false); // Initial load without notification
    }
    
    // Listen for branding updates
    const handleBrandingUpdate = () => {
      fetchClinicData(false);
    };
    
    // Listen for subscription updates
    const handleSubscriptionUpdate = () => {
      console.log('Subscription updated - refreshing sidebar data');
      fetchClinicData(true); // Show notification on subscription update
    };
    
    window.addEventListener('clinic-branding-updated', handleBrandingUpdate);
    window.addEventListener('subscription-updated', handleSubscriptionUpdate);
    
    return () => {
      window.removeEventListener('clinic-branding-updated', handleBrandingUpdate);
      window.removeEventListener('subscription-updated', handleSubscriptionUpdate);
    };
  }, [user]);

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => 
      prev.includes(groupKey) 
        ? prev.filter(key => key !== groupKey)
        : [...prev, groupKey]
    );
  };

  const isFeatureAvailable = (path) => {
    // Check if route has a mapped feature
    const featureKey = ROUTE_TO_FEATURE[path];
    
    // If no feature required (null), always available
    if (featureKey === null || featureKey === undefined) {
      return true;
    }
    
    // Enterprise/Premium/Growth tier (level 3) should have ALL features
    // This ensures top-tier customers never see locked features
    if (tierLevel >= 3) {
      return true;
    }
    
    // Check if the feature is enabled in the tier's features
    if (tierFeatures && typeof tierFeatures === 'object') {
      const isEnabled = tierFeatures[featureKey];
      if (isEnabled !== undefined) {
        return isEnabled === true;
      }
    }
    
    // Fallback to tier level check if feature not found in features object
    const requiredTier = FEATURE_TIERS[path] || 1;
    return tierLevel >= requiredTier;
  };

  const getRequiredTierName = (path) => {
    const featureKey = ROUTE_TO_FEATURE[path];
    
    // Check which tier has this feature enabled
    // This is a simplified version - ideally would check against all tiers
    if (featureKey) {
      // Features typically available at different tiers
      const advancedFeatures = ['ai_analytics_dashboard', 'audit_logs', 'wordpress_plugin', 'custom_integrations'];
      const proFeatures = ['advanced_analytics', 'consultations', 'secure_messaging', 'treatment_packages', 
                          'before_after_gallery', 'custom_branding', 'stripe_connect', 'role_based_access'];
      
      if (advancedFeatures.includes(featureKey)) {
        return 'Growth';
      }
      if (proFeatures.includes(featureKey)) {
        return 'Professional';
      }
    }
    
    const requiredLevel = FEATURE_TIERS[path] || 1;
    const tierNames = { 1: 'Starter', 2: 'Professional', 3: 'Growth' };
    return tierNames[requiredLevel];
  };

  const navGroups = [
    {
      key: 'main',
      label: 'Dashboard',
      icon: LayoutDashboard,
      items: [
        { to: '/admin', label: 'Overview', icon: LayoutDashboard, testid: 'nav-overview', permission: 'dashboard' },
        { to: '/admin/analytics', label: 'Analytics', icon: BarChart3, testid: 'nav-analytics', permission: 'reports' },
        { to: '/admin/reports', label: 'Reports', icon: TrendingUp, testid: 'nav-reports', permission: 'reports' },
        { to: '/admin/ai-analytics', label: 'AI Insights', icon: Brain, testid: 'nav-ai-analytics', badge: 'PRO', permission: 'reports' }
      ]
    },
    {
      key: 'patients',
      label: 'Patients',
      icon: Users,
      permission: 'patients',
      items: [
        { to: '/admin/members', label: 'Members', icon: Users, testid: 'nav-members', permission: 'patients' },
        { to: '/admin/appointments', label: 'Appointments', icon: Calendar, testid: 'nav-appointments', permission: 'appointments' },
        { to: '/admin/consultations', label: 'Consultations', icon: CalendarCheck, testid: 'nav-consultations', permission: 'appointments' },
        { to: '/admin/messages', label: 'Messages', icon: MessageSquare, testid: 'nav-messages', permission: 'communications' }
      ]
    },
    {
      key: 'membership',
      label: 'Membership',
      icon: Award,
      permission: 'memberships',
      items: [
        { to: '/admin/plans', label: 'Plans', icon: Award, testid: 'nav-plans', permission: 'memberships' },
        { to: '/admin/templates', label: 'AI Templates', icon: Sparkles, testid: 'nav-templates', permission: 'memberships' },
        { to: '/admin/packages', label: 'Packages', icon: Package, testid: 'nav-packages', permission: 'memberships' },
        { to: '/admin/fees', label: 'Fees', icon: DollarSign, testid: 'nav-fees', permission: 'billing' },
        { to: '/admin/payments', label: 'Payments', icon: CreditCard, testid: 'nav-payments', permission: 'billing' }
      ]
    },
    {
      key: 'clinic',
      label: 'Clinic',
      icon: Stethoscope,
      items: [
        { to: '/admin/services', label: 'Services', icon: Stethoscope, testid: 'nav-services', permission: 'memberships' },
        { to: '/admin/providers', label: 'Providers', icon: UserCog, testid: 'nav-providers', permission: 'team' },
        { to: '/admin/benefits', label: 'Benefits', icon: Gift, testid: 'nav-benefits', permission: 'memberships' },
        { to: '/admin/fulfillment', label: 'Fulfillment', icon: ClipboardCheck, testid: 'nav-fulfillment', permission: 'memberships' }
      ]
    },
    {
      key: 'marketing',
      label: 'Marketing',
      icon: Sparkles,
      adminOnly: true,
      items: [
        { to: '/admin/marketing', label: 'Marketing Hub', icon: Sparkles, testid: 'nav-marketing-hub', permission: 'communications' },
        { to: '/admin/referrals', label: 'Referral Program', icon: Share2, testid: 'nav-referrals', permission: 'communications' },
        { to: '/admin/promotions', label: 'Promotions', icon: Gift, testid: 'nav-promotions', permission: 'communications' },
        { to: '/admin/loyalty', label: 'Loyalty Program', icon: Crown, testid: 'nav-loyalty', permission: 'communications' },
        { to: '/admin/before-after', label: 'Gallery', icon: Camera, testid: 'nav-gallery', permission: 'communications' },
        { to: '/admin/wordpress', label: 'WordPress', icon: Globe, testid: 'nav-wordpress', badge: 'PRO', permission: 'communications' }
      ]
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: Settings,
      permission: 'settings',
      adminOnly: true,
      items: [
        { to: '/admin/settings', label: 'Clinic Settings', icon: Building2, testid: 'nav-settings', permission: 'settings' },
        { to: '/admin/branding', label: 'Branding', icon: ImageIcon, testid: 'nav-branding', permission: 'settings' },
        { to: '/admin/team', label: 'Team Management', icon: UsersRound, testid: 'nav-team', permission: 'team' },
        { to: '/admin/roles', label: 'Roles & Permissions', icon: Shield, testid: 'nav-roles', adminOnly: true },
        { to: '/admin/payment-gateways', label: 'Payment Gateways', icon: CreditCard, testid: 'nav-payment-gateways', permission: 'settings' },
        { to: '/admin/stripe-connect', label: 'Stripe Connect', icon: CreditCard, testid: 'nav-stripe', permission: 'billing' },
        { to: '/admin/audit', label: 'Audit Logs', icon: FileText, testid: 'nav-audit', badge: 'PRO', permission: 'settings' },
        { to: '/admin/subscription', label: 'Subscription', icon: Crown, testid: 'nav-subscription', adminOnly: true }
      ]
    }
  ];

  // Check if user has permission to view a menu item
  const hasItemPermission = (item) => {
    // Admin always has access
    if (isAdmin) return true;
    
    // If adminOnly, only admin can see
    if (item.adminOnly) return false;
    
    // If no permission required, allow
    if (!item.permission) return true;
    
    // Check specific permission
    return canView(item.permission);
  };

  // Filter menu groups and items based on permissions
  const filteredNavGroups = navGroups.map(group => {
    // Filter items within the group
    const filteredItems = group.items.filter(item => hasItemPermission(item));
    
    // Check if group itself should be visible
    const groupVisible = isAdmin || !group.adminOnly;
    
    if (!groupVisible || filteredItems.length === 0) {
      return null;
    }
    
    return { ...group, items: filteredItems };
  }).filter(Boolean);

  // Always expand the first available section when permissions finish loading
  useEffect(() => {
    if (!permissionsLoading && filteredNavGroups.length > 0) {
      const firstGroupKey = filteredNavGroups[0].key;
      setExpandedGroups(prev => {
        if (!prev.includes(firstGroupKey)) {
          return [firstGroupKey, ...prev];
        }
        return prev;
      });
    }
  }, [permissionsLoading, filteredNavGroups.length]);

  const renderNavItem = (item) => {
    const ItemIcon = item.icon;
    const isActive = location.pathname === item.to;
    const isAvailable = isFeatureAvailable(item.to);

    if (!isAvailable) {
      return (
        <div
          key={item.to}
          className={`flex items-center justify-between px-4 py-2.5 rounded-lg cursor-not-allowed ${isDark ? 'text-slate-500 bg-slate-700/30' : 'text-gray-400 bg-gray-50/50'}`}
          title={`Upgrade to ${getRequiredTierName(item.to)} to unlock`}
        >
          <div className="flex items-center space-x-3">
            <ItemIcon className="h-4 w-4" />
            <span className="text-sm">{item.label}</span>
          </div>
          <div className="flex items-center gap-1">
            <Lock className="h-3 w-3" />
            <span className="text-xs">Upgrade</span>
          </div>
        </div>
      );
    }

    return (
      <Link
        key={item.to}
        to={item.to}
        onClick={() => setIsMobileMenuOpen(false)}
        className={`flex items-center justify-between px-4 py-2.5 rounded-lg transition-colors ${
          isActive 
            ? 'font-medium' 
            : isDark ? 'text-slate-300 hover:bg-slate-700 hover:text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }`}
        style={isActive ? { 
          backgroundColor: isDark ? `${brandingColors.primary}25` : `${brandingColors.primary}15`,
          color: brandingColors.primary 
        } : {}}
        data-testid={item.testid}
      >
        <div className="flex items-center space-x-3">
          <ItemIcon className="h-4 w-4" />
          <span className="text-sm">{item.label}</span>
        </div>
        {item.badge && (
          <span 
            className="px-1.5 py-0.5 text-xs font-medium rounded"
            style={{ 
              backgroundColor: `${brandingColors.primary}20`,
              color: brandingColors.primary 
            }}
          >
            {item.badge}
          </span>
        )}
      </Link>
    );
  };

  const clinicName = clinicData?.name || user?.clinic_name || 'My Clinic';
  // Logo URL can be base64 data URL or regular URL - use directly
  const clinicLogo = clinicData?.logo_url || clinicData?.branding?.logo_url;

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg border"
      >
        {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 w-64 border-r z-40 transform transition-all duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
        <div className="flex flex-col h-full">
          {/* Header with Clinic Info - uses branding colors */}
          <div 
            className={`p-4 border-b ${isDark ? 'border-slate-700' : ''}`}
            style={{ 
              background: isDark 
                ? `linear-gradient(135deg, ${brandingColors.primary}20, ${brandingColors.secondary}10, #1e293b)`
                : `linear-gradient(135deg, ${brandingColors.primary}15, ${brandingColors.secondary}08, white)`
            }}
          >
            {/* Clinic Logo & Name */}
            <div className="flex items-center gap-3 mb-3">
              {clinicLogo ? (
                <img 
                  src={clinicLogo} 
                  alt={clinicName}
                  className="h-10 w-10 rounded-lg object-cover border shadow-sm"
                />
              ) : (
                <div 
                  className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-sm"
                  style={{ backgroundColor: brandingColors.primary }}
                >
                  {clinicName.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h2 className={`font-bold truncate text-sm leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {clinicName}
                </h2>
                <p className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  by Clinicax Systems
                </p>
              </div>
            </div>

            {/* Subscription Badge */}
            <div className="flex items-center gap-2">
              <Badge 
                className={`text-xs capitalize border transition-all duration-300 ${TIER_COLORS[clinicTier] || TIER_COLORS.starter} ${
                  tierBadgeAnimating 
                    ? 'animate-pulse ring-2 ring-offset-1 ring-yellow-400 scale-110' 
                    : ''
                }`}
                data-testid="sidebar-tier-badge"
              >
                <Crown className={`h-3 w-3 mr-1 ${tierBadgeAnimating ? 'animate-spin' : ''}`} />
                {clinicTierName}
              </Badge>
              {tierLevel < 3 && (
                <Link 
                  to="/admin/subscription" 
                  className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1 font-medium"
                >
                  <Zap className="h-3 w-3" />
                  Upgrade
                </Link>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {permissionsLoading ? (
              // Skeleton loader while permissions are loading
              <div className="space-y-2 animate-pulse">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="space-y-1">
                    <div className={`h-10 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}></div>
                    {i <= 2 && (
                      <div className="ml-6 space-y-1">
                        <div className={`h-8 rounded-lg w-5/6 ${isDark ? 'bg-slate-600' : 'bg-gray-50'}`}></div>
                        <div className={`h-8 rounded-lg w-4/6 ${isDark ? 'bg-slate-600' : 'bg-gray-50'}`}></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              filteredNavGroups.map((group) => {
              const isExpanded = expandedGroups.includes(group.key);
              const GroupIcon = group.icon;
              const hasActiveItem = group.items.some(item => location.pathname === item.to);
              
              return (
                <div key={group.key} className="mb-1">
                  <button
                    onClick={() => toggleGroup(group.key)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                      hasActiveItem 
                        ? isDark ? 'text-white' : 'text-gray-800'
                        : isDark ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-gray-100 text-gray-700'
                    }`}
                    style={hasActiveItem ? { 
                      backgroundColor: isDark ? `${brandingColors.primary}30` : `${brandingColors.primary}12`,
                      color: brandingColors.primary 
                    } : {}}
                  >
                    <div className="flex items-center space-x-3">
                      <GroupIcon className="h-5 w-5" />
                      <span className="text-sm font-medium">{group.label}</span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  
                  {isExpanded && (
                    <div className={`mt-1 ml-3 space-y-0.5 border-l-2 pl-2 ${isDark ? 'border-slate-600' : 'border-gray-100'}`}>
                      {group.items.map(renderNavItem)}
                    </div>
                  )}
                </div>
              );
            })
            )}
          </nav>

          {/* User Section */}
          <div className={`p-4 border-t ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-gray-50'}`}>
            <div className="mb-3 flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.full_name}</p>
                <p className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{user.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <ThemeToggle />
                <NotificationsDropdown />
              </div>
            </div>
            <Button onClick={onLogout} variant="outline" size="sm" className={`w-full ${isDark ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : ''}`} data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminSidebar;
